// The browser tab's name inside a client environment (#10718).
//
// The client environment serves the same app as the staff member's own, so
// in the tab strip, the window list and history the two read the same. Here
// the tab is named for the client, ahead of whatever it was called: the
// product's name, or the name a page gave it. Pages name the tab as they
// mount and put the product's name back as they leave (`useDocumentTitle`),
// so the title is watched and the client's name put back in front each time
// it changes. On leaving the client environment the title is what it would
// have been without this.
import { useEffect } from "react";

const SEPARATOR = " · ";

/** `title` named for `client`; unchanged when it already is. */
function titleInClient(client: string, title: string): string {
  if (title === client || title.startsWith(`${client}${SEPARATOR}`)) {
    return title;
  }
  return title === "" ? client : `${client}${SEPARATOR}${title}`;
}

/** `title` without `client`'s name in front; unchanged when it has none. */
function titleOutsideClient(client: string, title: string): string {
  if (title === client) return "";
  const prefix = `${client}${SEPARATOR}`;
  return title.startsWith(prefix) ? title.slice(prefix.length) : title;
}

export function useClientEnvironmentTitle(client: string): void {
  useEffect(() => {
    const name = (): void => {
      const named = titleInClient(client, document.title);
      if (document.title !== named) document.title = named;
    };
    name();
    const observer = new MutationObserver(name);
    // Read after naming: assigning a title creates the element when the
    // document had none.
    const title = document.querySelector("title");
    if (title) {
      observer.observe(title, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
    return () => {
      observer.disconnect();
      document.title = titleOutsideClient(client, document.title);
    };
  }, [client]);
}
